import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, inr, fmtDate, loadRazorpay } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Chip } from "../components/ui";
import {
  Crown, Star, Shield, Zap, BarChart, Code, Lock, Users,
  Download, Globe, Brain, Palette, ArrowRight, Check,
  X, CreditCard, Calendar, RotateCcw, FileText, ArrowLeft, Info,
} from "lucide-react";

const PLANS = {
  starter: {
    id: "starter",
    name: "Cricso Starter",
    emoji: "\uD83C\uDFD3",
    tagline: "Perfect for small cricket groups",
    badge: "Free",
    badgeColor: "success",
    maxClubs: 2,
    maxMembersPerClub: 30,
    priceMonthly: 0,
    priceYearly: 0,
    yearlySavings: 0,
    accent: "from-emerald-500 to-emerald-600",
    accentLight: "bg-emerald-50",
    accentText: "text-emerald-700",
    badgeBg: "bg-emerald-100 text-emerald-700",
    features: [
      { icon: Users, label: "Up to 2 Clubs" },
      { icon: Users, label: "Up to 30 Members per Club" },
      { icon: Shield, label: "Match Polls" },
      { icon: Zap, label: "Team Generation" },
      { icon: Shield, label: "Expense Tracking" },
      { icon: Shield, label: "Match Fee Split" },
      { icon: BarChart, label: "Basic Ledger" },
      { icon: Globe, label: "Join Links" },
      { icon: Users, label: "Community Support" },
    ],
  },
  league: {
    id: "league",
    name: "Cricso League",
    emoji: "\uD83E\uDD48",
    tagline: "Ideal for regular cricket organizers",
    badge: "Popular",
    badgeColor: "default",
    maxClubs: 5,
    maxMembersPerClub: 50,
    priceMonthly: 300,
    priceYearly: 2400,
    yearlySavings: 1200,
    accent: "from-blue-500 to-blue-600",
    accentLight: "bg-blue-50",
    accentText: "text-blue-700",
    badgeBg: "bg-blue-100 text-blue-700",
    features: [
      { icon: Users, label: "Up to 5 Clubs" },
      { icon: Users, label: "Up to 50 Members per Club" },
      { icon: Shield, label: "Match Polls" },
      { icon: Zap, label: "Team Generation" },
      { icon: Shield, label: "Expense Tracking" },
      { icon: Shield, label: "Match Fee Split" },
      { icon: BarChart, label: "Basic Ledger" },
      { icon: Globe, label: "Join Links" },
      { icon: Users, label: "Community Support" },
      { icon: Crown, label: "Tournament Management" },
      { icon: Globe, label: "Merchandise Store" },
      { icon: Download, label: "CSV Export" },
      { icon: BarChart, label: "Advanced Reports" },
      { icon: Shield, label: "Payment Tracking" },
      { icon: Star, label: "Priority Support" },
    ],
  },
  premier: {
    id: "premier",
    name: "Cricso Premier",
    emoji: "\uD83C\uDFC6",
    tagline: "Built for academies, tournaments & businesses",
    badge: "Best Value",
    badgeColor: "warning",
    maxClubs: 10,
    maxMembersPerClub: 100,
    priceMonthly: 500,
    priceYearly: 4800,
    yearlySavings: 1200,
    accent: "from-amber-500 to-amber-600",
    accentLight: "bg-amber-50",
    accentText: "text-amber-700",
    badgeBg: "bg-amber-100 text-amber-700",
    features: [
      { icon: Users, label: "Up to 10 Clubs" },
      { icon: Users, label: "Up to 100 Members per Club" },
      { icon: Shield, label: "Match Polls" },
      { icon: Zap, label: "Team Generation" },
      { icon: Shield, label: "Expense Tracking" },
      { icon: Shield, label: "Match Fee Split" },
      { icon: BarChart, label: "Basic Ledger" },
      { icon: Globe, label: "Join Links" },
      { icon: Users, label: "Community Support" },
      { icon: Crown, label: "Tournament Management" },
      { icon: Globe, label: "Merchandise Store" },
      { icon: Download, label: "CSV Export" },
      { icon: BarChart, label: "Advanced Reports" },
      { icon: Shield, label: "Payment Tracking" },
      { icon: Star, label: "Priority Support" },
      { icon: Brain, label: "AI Team Balancing" },
      { icon: Brain, label: "AI Match Insights" },
      { icon: Palette, label: "Custom Branding" },
      { icon: BarChart, label: "Advanced Analytics" },
      { icon: Users, label: "Multi-Admin Management" },
      { icon: Code, label: "API Access (future)" },
      { icon: Star, label: "Early Access Features" },
      { icon: Star, label: "Premium Support" },
    ],
  },
};

const PLAN_ORDER = ["starter", "league", "premier"];

const FAQS = [
  { q: "Can I cancel anytime?", a: "Yes. Your plan stays active until the end of the current billing period." },
  { q: "Will my data be lost after downgrade?", a: "No. Existing clubs remain accessible, but you can't create new ones beyond your plan limit." },
  { q: "Can I upgrade later?", a: "Yes. Upgrades apply immediately with prorated charges for monthly plans." },
  { q: "What payment methods are accepted?", a: "UPI, Credit/Debit Cards, Net Banking, and Wallets via Razorpay. All secure and GST-compliant." },
];

function fmtINR(n) {
  return "\u20B9" + n.toLocaleString("en-IN");
}

export default function PlanDetailsPage() {
  const nav = useNavigate();
  const { planId } = useParams();
  const { user, refreshMe } = useAuth();
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [currentPlan, setCurrentPlan] = useState("starter");
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const plan = PLANS[planId] || PLANS.starter;
  const isCurrentPlan = currentPlan === planId;
  const isUpgrade = PLAN_ORDER.indexOf(planId) > PLAN_ORDER.indexOf(currentPlan);
  const isDowngrade = PLAN_ORDER.indexOf(planId) < PLAN_ORDER.indexOf(currentPlan);

  const price = billingCycle === "yearly" ? plan.priceYearly : plan.priceMonthly;
  const perMonth = billingCycle === "yearly" ? Math.round(plan.priceYearly / 12) : plan.priceMonthly;

  useEffect(() => {
    const el = document.querySelector(".app-shell");
    if (el) el.scrollTop = 0;
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (user) fetchSubscription();
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const { data } = await api.get("/subscription/current");
      setSubscription(data);
      setCurrentPlan(data.plan_id || "starter");
    } catch {
      setCurrentPlan("starter");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!user) { nav("/login", { state: { from: `/pricing/${planId}` } }); return; }
    setActionLoading(true);
    try {
      if (isCurrentPlan) return;
      if (isUpgrade) {
        const { data } = await api.post("/subscription/create", {
          plan_id: planId,
          billing_cycle: billingCycle,
        });
        if (data.requires_payment) {
          await loadRazorpay();
          const options = {
            key: data.key_id,
            amount: data.amount,
            currency: data.currency,
            name: "Cricso",
            description: `${plan.name} - ${billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1)}`,
            order_id: data.order_id,
            handler: async (response) => {
              await api.post("/subscription/verify-payment", {
                payment_id: data.payment_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              });
              toast.success("Subscription activated!");
              await refreshMe();
              nav("/home", { replace: true });
            },
            prefill: { email: user.email, name: user.name },
            theme: { color: "#2E7D32" },
          };
          const rzp = new window.Razorpay(options);
          rzp.on("payment.failed", (response) => {
            toast.error("Payment failed: " + response.error.description);
            setActionLoading(false);
          });
          rzp.open();
        } else {
          toast.success("Subscription activated!");
          await refreshMe();
          nav("/home", { replace: true });
        }
      } else {
        await api.post("/subscription/change", {
          plan_id: planId,
          billing_cycle: billingCycle,
        });
        toast.success("Plan will change at period end");
        await refreshMe();
        setCurrentPlan(planId);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="app-shell flex flex-col min-h-screen bg-surface">
        <div className="flex-1 grid place-items-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell flex flex-col min-h-screen bg-surface">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-surface/80 backdrop-blur-lg border-b border-black/[0.04]">
        <div className="flex items-center gap-3 px-4 h-14">
          <button
            onClick={() => nav(-1)}
            className="h-9 w-9 rounded-full hover:bg-black/5 active:bg-black/10 grid place-items-center transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} className="text-ink" />
          </button>
          <h1 className="text-lg font-bold text-ink">Plan Details</h1>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Plan Hero Card */}
        <div className="px-4 pt-5 pb-4">
          <div
            className={`rounded-2xl overflow-hidden bg-white border ${plan.accent.replace("from-", "border-").replace("to-", "").split(" ")[0]}-200 shadow-sm`}
          >
            <div className={`h-1.5 w-full bg-gradient-to-r ${plan.accent}`} />
            <div className="px-5 py-5">
              {/* Plan name + badge */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2.5">
                  <span className="text-3xl">{plan.emoji}</span>
                  <div>
                    <h2 className="text-xl font-extrabold text-ink tracking-tight">{plan.name}</h2>
                    <p className="text-ink-muted text-[13px]">{plan.tagline}</p>
                  </div>
                </div>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${plan.badgeBg}`}>
                  {plan.badge}
                </span>
              </div>

              {/* Price */}
              <div className="mt-4 mb-3">
                {price === 0 ? (
                  <div className="text-[36px] font-extrabold text-ink leading-none">FREE</div>
                ) : (
                  <div className="flex items-baseline gap-1">
                    <span className="text-[36px] font-extrabold text-ink leading-none tracking-tight">
                      {fmtINR(price)}
                    </span>
                    <span className="text-ink-muted text-sm font-medium">
                      /{billingCycle === "yearly" ? "yr" : "mo"}
                    </span>
                  </div>
                )}
                {price > 0 && billingCycle === "yearly" && (
                  <p className={`text-sm font-medium mt-1 ${plan.accentText}`}>
                    {fmtINR(perMonth)}/mo • Save {fmtINR(plan.yearlySavings)}/yr
                  </p>
                )}
              </div>

              {/* Limits */}
              <div className="flex items-center gap-2 mb-4">
                <span className="text-[12px] font-semibold text-ink-muted bg-black/[0.04] rounded-full px-2.5 py-1">
                  {plan.maxClubs} club{plan.maxClubs > 1 ? "s" : ""} max
                </span>
                <span className="text-[12px] font-semibold text-ink-muted bg-black/[0.04] rounded-full px-2.5 py-1">
                  {plan.maxMembersPerClub} members/club
                </span>
              </div>

              {/* Billing toggle */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setBillingCycle("monthly")}
                  className={`flex-1 h-10 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    billingCycle === "monthly"
                      ? "bg-brand text-white shadow-sm"
                      : "bg-black/[0.04] text-ink-muted"
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle("yearly")}
                  className={`flex-1 h-10 rounded-xl text-sm font-semibold transition-all duration-200 relative ${
                    billingCycle === "yearly"
                      ? "bg-brand text-white shadow-sm"
                      : "bg-black/[0.04] text-ink-muted"
                  }`}
                >
                  Yearly
                  {billingCycle === "yearly" && (
                    <span className="absolute -top-2 right-2 bg-white text-brand text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                      Save
                    </span>
                  )}
                </button>
              </div>

              {/* CTA Button */}
              <Button
                className="w-full"
                size="lg"
                variant={isCurrentPlan ? "outline" : "primary"}
                disabled={isCurrentPlan || actionLoading}
                onClick={handleUpgrade}
                data-testid="plan-upgrade-btn"
              >
                {actionLoading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Processing\u2026
                  </>
                ) : isCurrentPlan ? (
                  "Current Plan"
                ) : isUpgrade ? (
                  <>
                    <ArrowRight size={18} /> Upgrade Now
                  </>
                ) : (
                  <>
                    <ArrowRight size={18} /> Switch Plan
                  </>
                )}
              </Button>
              {isDowngrade && (
                <p className="text-center text-xs text-ink-muted mt-2">
                  Takes effect at period end
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Subscription Info */}
        {subscription?.current_period_end && (
          <div className="px-4 pb-4">
            <Card>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-ink-muted">
                  <Calendar size={14} />
                  <span>
                    {subscription?.cancel_at_period_end ? "Cancels on" : "Renews on"}{" "}
                    {fmtDate(subscription.current_period_end)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {subscription?.cancel_at_period_end ? (
                    <Button variant="primary" size="sm" onClick={async () => {
                      try { await api.post("/subscription/reactivate"); toast.success("Reactivated"); fetchSubscription(); } catch (e) { toast.error(apiError(e)); }
                    }}>
                      <RotateCcw size={14} /> Reactivate
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={async () => {
                      if (!window.confirm("Cancel subscription?")) return;
                      try { await api.post("/subscription/cancel"); toast.success("Will cancel at period end"); fetchSubscription(); } catch (e) { toast.error(apiError(e)); }
                    }}>
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Features */}
        <div className="px-4 pb-4">
          <h3 className="text-base font-bold text-ink mb-3">What's included</h3>
          <Card>
            <div className="divide-y divide-black/[0.04]">
              {plan.features.map((f, i) => (
                <div key={i} className="flex items-center gap-3 py-3">
                  <div className={`h-8 w-8 rounded-lg ${plan.accentLight} grid place-items-center shrink-0`}>
                    <f.icon size={16} className={plan.accentText} />
                  </div>
                  <span className="text-sm font-medium text-ink">{f.label}</span>
                  <Check size={16} className="text-brand ml-auto shrink-0" />
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* FAQ */}
        <div className="px-4 pb-4">
          <h3 className="text-base font-bold text-ink mb-3">Questions?</h3>
          <div className="space-y-2">
            {FAQS.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-10 text-center">
          <p className="text-[11px] text-ink-muted/60">
            All prices in INR. GST extra as applicable.
          </p>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border transition-colors duration-200 ${open ? "border-black/10 bg-white" : "border-transparent bg-white/60"}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3.5 text-left"
      >
        <span className="text-[14px] font-semibold text-ink pr-3">{q}</span>
        <Info size={18} className={`text-ink-muted shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ease-out ${open ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}>
        <p className="px-4 pb-3.5 text-[13px] leading-relaxed text-ink-muted">{a}</p>
      </div>
    </div>
  );
}
